import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { Modal, Spin } from "antd";

import Moment from "moment";
import "moment-timezone";
import { extendMoment } from "moment-range";

import useRobotTimezone from "../../libs/useRobotTimezone";

import {
  getRobotWeld,
  getRobotWeldTestReportsStatus,
  RobotSliceType,
  WeldTestReportStatus,
} from "./robotSlice";

import RobotWeldTestReportForm from "./RobotWeldTestReportForm";
import LoadingPage from "../../components/LoadingPage";
import Calendar from "./Calendar";
import { LOADING_ANIMATION_SIZE_FULL } from "../../libs/constants";
import { useLoadingWithProgress } from "../../libs/useLoading";
import { groupWeldRunReports } from "./videosAndReports/groupWeldRun";

// @ts-ignore
const moment = extendMoment(Moment);

function RobotWeldTestReportsCalendar() {
  const dispatch = useDispatch();

  const { robotName } = useParams();
  const robotId =
    robotName && robotName.match(/(\d+)/) && robotName.match(/(\d+)/)[0];
  const robotTimezone = useRobotTimezone(true);

  const [[robotWeldRun, reportStatus], loadingProgress] =
    useLoadingWithProgress<
      [unknown[] | undefined, WeldTestReportStatus[] | undefined],
      RobotSliceType
    >(
      "robot",
      ({ weldRun, weldTestReportsStatus }) => [
        weldRun?.[robotId],
        weldTestReportsStatus?.[robotId],
      ],
      [getRobotWeld, getRobotWeldTestReportsStatus],
    );

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [groupedWeldRun, setGroupedWeldRun] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [activeDateRange, setActiveDateRange] = useState({
    start: moment().startOf("month").format("YYYY-MM-DD"),
    end: moment().endOf("month").format("YYYY-MM-DD"),
  });
  const { start, end } = activeDateRange;

  useEffect(() => {
    dispatch(getRobotWeld({ id: robotId, start, end }));
    dispatch(getRobotWeldTestReportsStatus({ id: robotId, start, end }));
  }, [dispatch, robotId, start, end]);

  // group qc status by weld id for lookup
  const existingReports = (reportStatus ?? []).filter((e) =>
    Array.isArray(e.reports),
  );
  const reportStatusGrouped = {};
  for (const entry of existingReports) {
    const validReports: object[] = entry.reports.filter(
      (e) => e && typeof e === "object" && !e.deleted_at,
    );
    if (validReports.length) {
      reportStatusGrouped[entry.id] = validReports.map(
        (e: any) => e.test_status,
      );
    }
  }

  useEffect(() => {
    setGroupedWeldRun(
      groupWeldRunReports(robotWeldRun,robotTimezone)
    );
  }, [robotWeldRun, robotTimezone]);

  // filter and count events by qc status (current date range)
  if (!reportStatusGrouped || !start) return;

  const reportStatusCounts = {
    pass: 0,
    fail: 0,
    "not tested": 0,
    "no report": 0,
  };
  const filteredGroups = groupedWeldRun.filter((entry) => {
    const runs = entry?.children;
    if (!runs) {
      return false;
    }

    const ids = runs.map((e) => e.id);

    let status = getQcStatus(ids, reportStatusGrouped);
    reportStatusCounts[status]++;

    // if no filter set, return all
    if (statusFilter === null) return true;

    // otherwise only return the type matching filter
    return status === statusFilter;
  });

  return (
    <Spin
      spinning={!loadingProgress.isFinished()}
      indicator={
        <LoadingPage
          size={LOADING_ANIMATION_SIZE_FULL}
          progress={loadingProgress}
        />
      }
    >
      <Calendar
        className="weld-qc-report"
        onDateChange={(calendarStart: string, calendarEnd: string) => {
          setActiveDateRange({ start: calendarStart, end: calendarEnd });
        }}
        overlay={{
          title: "Submitted:",
          alertRed: { value: reportStatusCounts.fail },
          alertGreen: { value: reportStatusCounts.pass },
          alertYellow: {
            label: "Not Tested",
            value: reportStatusCounts["not tested"],
          },
          alertOther: {
            label: "Without Reports: ",
            value: reportStatusCounts["no report"],
          },
          onChange: (button) => {
            if (button === "green") {
              setStatusFilter("pass");
            } else if (button === "red") {
              setStatusFilter("fail");
            } else if (button === "yellow") {
              setStatusFilter("not tested");
            } else if (button === "other") {
              setStatusFilter("no report");
            } else {
              setStatusFilter(null);
            }
          },
        }}
        events={filteredGroups}
        eventClick={(info) => {
          setSelectedJob({
            start: info.event.start,
            end: info.event.end,
            ...info.event.extendedProps,
          });

          setModalOpen(true);
        }}
        eventClassNames={({ event }) => {
          try {
            // each event on calendar can have multiple welds
            const ids = event.extendedProps.children.map((e) => e.id);
            const status = getQcStatus(ids, reportStatusGrouped);
            switch (status) {
              case "pass":
                return ["event-indicator-pass"];
              case "fail":
                return ["event-indicator-fail"];
              case "not tested":
                return ["event-indicator-not-tested"];
              default:
                return ["event-indicator-no-data"];
            }
          } catch (e) {
            // console.warn("error in robotweldtestreports/eventclassnames", e)
            return [];
          }
        }}
      />
      {!!selectedJob && (
        <Modal
          className="WeldTestReport_Modal"
          title={`Weld Test Report of ${selectedJob.work_order_ref} ${selectedJob.spool_ref} ${selectedJob.welder_id_ref}`}
          open={modalOpen}
          // onOk={handleOk}
          onCancel={() => setModalOpen(false)}
          footer={null}
          width={800}
          style={{ width: "80vw" }}
          destroyOnClose={true}
        >
          <RobotWeldTestReportForm job={selectedJob}></RobotWeldTestReportForm>
        </Modal>
      )}
    </Spin>
  );
}

function getQcStatus(ids, reportStatusGrouped) {
  let status = "no report";
  for (const id of ids) {
    const reportStatuses = reportStatusGrouped[id];
    if (reportStatuses) {
      // If any FAIL report submitted (regardless of the rest) → 1 FAIL
      if (reportStatuses.includes("fail")) {
        status = "fail";
      }
      // if no FAIL and any number of PASS report is submitted → 1 PASS
      else if (status !== "fail" && reportStatuses.includes("pass")) {
        status = "pass";
      }
      // If there is a not tested reported submitted (no FAIL or PASS report found) → 1 NOT TESTED
      else if (status !== "fail" && status !== "pass") {
        status = "not tested";
      }
    }
    // if no report submitted → 1 No Reports/Not submitted
  }
  return status;
}

export default RobotWeldTestReportsCalendar;
